import React, { Fragment, useEffect, useRef } from "react"
import ReactDOM from "react-dom"
import { Dialog, Transition } from "@headlessui/react"

// Use a ternary operator to make sure that the document object is defined
const portalRoot =
	typeof document !== `undefined` ? document.getElementById("portal") : null

export default function Modal({
	isOpen,
	onClose,
	icon,
	title,
	buttons,
	children,
}) {
	if (!isOpen) return null

	const cancelButtonRef = useRef(null)
	const el =
		typeof document !== `undefined` ? document.createElement("div") : null

	useEffect(() => {
		portalRoot.appendChild(el)
		// Specify how to clean up after this effect:
		return function cleanup() {
			portalRoot.removeChild(el)
		}
	})

	return ReactDOM.createPortal(
		<Transition.Root show={isOpen} as={Fragment}>
			<Dialog
				as="div"
				className="fixed z-10 inset-0 overflow-y-auto"
				onClose={onClose}
			>
				<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
					</Transition.Child>

					{/* This element is to trick the browser into centering the modal contents. */}
					<span
						className="hidden sm:inline-block sm:align-middle sm:h-screen"
						aria-hidden="true"
					>
						&#8203;
					</span>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enterTo="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 translate-y-0 sm:scale-100"
						leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									{icon}
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<Dialog.Title
											as="h3"
											className="text-lg leading-6 font-medium text-gray-900"
										>
											{title}
										</Dialog.Title>
										<div className="mt-2">{children}</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								{buttons}
							</div>
						</div>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition.Root>,
		document.body
	)
}
